/* eslint-disable @next/next/no-img-element */
import React from 'react';
import IconFont from '$/components/IconFont';
import { handleImageUrl } from '$/utils/image';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import { useMyIpCreationCopy } from '$/pages/RoboneoMyIps/useMyIpCreationCopy';
import { mtstatTrack } from '$/utils/mtstat';
import {
  type ChooseIPEntryType,
  openChooseIPModal,
} from '$/components/ChooseIpModal/chooseIPStore';
import { getEnv, navigate } from '@/common/agents';
import { getViralRemakeRoute } from '@/common/viralRemakeRoute';
import style from './index.module.scss';

const CREATION_TOOLS = [
  {
    id: 'short-drama',
    titleKey: 'shortDramaTitle',
    descriptionKey: 'shortDramaDescription',
    cover: 'https://roboneo-public.meitudata.com/public/6d8a144a-46a0-448d-8b94-b82bb396d6d5.jpg',
    iconName: 'nodeAiVideo',
    promptKey: '',
    entryType: 'microdrama',
    trackingType: 'ai_short_video',
  },
  {
    id: 'viral-remake',
    titleKey: 'viralRemakeTitle',
    descriptionKey: 'viralRemakeDescription',
    cover: 'https://roboneo-public.meitudata.com/public/6c31bfa4-97ec-4ece-8eec-c705525c6dd9.jpg',
    iconName: 'musicV2Bold',
    promptKey: '',
    entryType: 'viral_remake',
    trackingType: 'viral_remake',
  },
  {
    id: 'sticker',
    titleKey: 'stickerTitle',
    descriptionKey: 'stickerDescription',
    cover: 'https://roboneo-public.meitudata.com/public/36affe4f-9fc6-4e17-894f-f14b344c21e6.jpg',
    iconName: 'kid',
    promptKey: 'generate_a_set_of_emojis_with_ip_name_1',
    entryType: 'emoji',
    trackingType: 'emoji',
  },
  {
    id: 'daily-video',
    titleKey: 'dailyVideoTitle',
    descriptionKey: 'dailyVideoDescription',
    cover: 'https://roboneo-public.meitudata.com/public/e693b6df-ddea-4078-95b3-1742f13bf76a.jpg',
    iconName: 'mobilePhoneV2Bold',
    promptKey: 'generate_a_short_video_with_ip_name_1_ip_name_2',
    entryType: 'shortvideo',
    trackingType: 'ai_daily_video',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  titleKey: keyof ReturnType<typeof useMyIpCreationCopy>['home'];
  descriptionKey: keyof ReturnType<typeof useMyIpCreationCopy>['home'];
  cover: string;
  iconName: string;
  entryType: ChooseIPEntryType;
  promptKey: string;
  trackingType: 'ai_short_video' | 'ai_daily_video' | 'viral_remake' | 'emoji';
}>;

type ToolContentProps = {
  tool: { title: string; description: string; cover: string; iconName: string; };
  onClick?: () => void;
};

/** 按封面比例加载缩略图，并在传入点击回调时提供可交互按钮。 */
function ToolContent({ tool, onClick }: ToolContentProps) {
  const content = (
    <>
      <div className={style.coverWrap} aria-hidden="true">
        <img
          className={style.cover}
          src={handleImageUrl(tool.cover, { size: 326, mode: 3 })}
          alt=""
          draggable={false}
        />
      </div>
      <div className={style.cardInfo}>
        <span className={style.iconWrap} aria-hidden="true">
          <IconFont
            className={style.icon}
            name={tool.iconName}
            fontSize={20}
            color="var(--content-primary, rgb(247 248 250 / 96%))"
          />
        </span>
        <div className={style.copy}>
          <h3 className={style.cardTitle}>{tool.title}</h3>
          <p className={style.description}>{tool.description}</p>
        </div>
      </div>
    </>
  );

  if (!onClick) return content;

  return (
    <button type="button" className={style.toolContentButton} onClick={onClick}>
      {content}
    </button>
  );
}

/**
 * 渲染首页创作入口，并将场景及提示词交给角色选择流程。
 */
export default function CreateContentBox() {
  const { home } = useMyIpCreationCopy();
  const language = useLanguageStore((state) => state.languageStore);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useOfficialStore((state) => state.toLogin);

  /** 点击卡片上报入口；爆款复刻直接进入素材准备页，其余场景继续选择角色。 */
  const handleChooseIp = (
    entryType: ChooseIPEntryType,
    promptKey: string,
    trackingType: (typeof CREATION_TOOLS)[number]['trackingType'],
  ) => {
    mtstatTrack('roboneo_create_content_myip_click', { type: trackingType });
    if (!isUserLoggedIn) {
      toLogin().catch(() => {});
      return;
    }

    if (entryType === 'viral_remake') {
      navigate({ mode: 'router', pathname: getViralRemakeRoute(getEnv().ENV === 'development') });
      return;
    }

    const prompt = promptKey
      ? language.smartcatTranslationText((value) => value[promptKey]).replace('{ip_name}', '{ip_names}')
      : '';
    openChooseIPModal(entryType, prompt, trackingType);
  };

  return (
    <section className={style.create_content_box_container} aria-labelledby="create-content-title">
      <div className={style.header}>
        <h2 id="create-content-title" className={style.title}>{home.contentTitle}</h2>
      </div>

      <div className={style.toolList}>
        {CREATION_TOOLS.map((tool) => (
          <li key={tool.id} className={style.toolCard}>
            <ToolContent
              tool={{ ...tool, title: home[tool.titleKey], description: home[tool.descriptionKey] }}
              onClick={() => handleChooseIp(tool.entryType, tool.promptKey, tool.trackingType)}
            />
          </li>
        ))}
      </div>
    </section>
  );
}
